/**
 * assets/product-finder.js
 *
 * Cascading Equipment Make -> Engine Model -> Part Type dropdowns for the
 * Hero image section's product finder block, plus the redirect to the
 * filtered collection page on submit.
 *
 * DATA SOURCE: snippets/hero-product-finder-data.liquid renders one page
 * of flat product records (id/handle/equipment_make/engine_model/part_type)
 * for products that have a non-blank custom.equipment_make. Liquid can only
 * see up to 250 products per render (Shopify's paginate ceiling — a plain
 * `for` loop without paginate is capped at 50, and paginate itself can't go
 * higher than 250), so for a catalog bigger than that, this file fetches
 * every remaining page via the Section Rendering API and merges the
 * records client-side before building the make -> models/types map.
 *
 * FILTER_PARAM_MAP + EXTRA_PARAMS confirmed against the live collection
 * page's own filter URLs: Equipment Make and Engine Model are Boost
 * metafield filters (`filter.p.m.custom.<key>`), Part Type is Shopify's
 * native product type filter (`filter.p.product_type`), and `view` pins
 * the redirect to the ecom-collection-a template/view.
 */
window.HeroProductFinder = (function () {
  var FILTER_PARAM_MAP = {
    equipment_make: 'filter.p.m.custom.equipment_make',
    engine_model: 'filter.p.m.custom.engine_models',
    part_type: 'filter.p.product_type',
  };

  // Always included on the redirect, regardless of which fields were set.
  var EXTRA_PARAMS = {
    view: 'ecom-collection-a',
  };

  var ALL_MODELS_LABEL = 'All Engine Models';
  var ALL_TYPES_LABEL = 'All Part Types';

  function init(blockId) {
    var root = document.getElementById('HeroProductFinder-' + blockId);
    if (!root) return;

    var form = root.querySelector('[data-finder-form]');
    var makeSelect = root.querySelector('[data-finder-field="equipment_make"]');
    var modelSelect = root.querySelector('[data-finder-field="engine_model"]');
    var partSelect = root.querySelector('[data-finder-field="part_type"]');
    var submitBtn = root.querySelector('[data-finder-submit]');
    var collectionUrl = root.getAttribute('data-collection-url');
    var scanUrl = root.getAttribute('data-scan-url');
    var sectionId = root.getAttribute('data-section-id');

    var page1 = readPageData(blockId);
    if (!page1) return;

    loadAllPages(page1, scanUrl, sectionId, blockId).then(function (rawProducts) {
      // Trim once, here, rather than scattering .trim() through every
      // comparison/URL-building call site below. A stray trailing space in
      // a merchant-entered metafield value (e.g. "4045 4.5L John Deere ")
      // would otherwise flow straight through into the redirect URL as a
      // literal trailing "+" (query-string encoding of a space), which
      // Boost then fails to match against its clean filter value.
      var allProducts = normalizeProducts(rawProducts);
      console.log('[Product finder] merged product records (' + allProducts.length + ' with equipment_make set):', allProducts);

      var makes = getMakes(allProducts);
      console.log('[Product finder] equipment makes found:', makes);

      var storageKey = 'heroProductFinder:' + blockId;

      function saveSelection() {
        try {
          sessionStorage.setItem(storageKey, JSON.stringify({
            make: makeSelect.value,
            model: modelSelect.value,
            type: partSelect.value,
          }));
        } catch (e) {
          // Storage unavailable (private mode, quota, etc.) — selection
          // just won't survive navigation, no functional loss otherwise.
        }
      }

      function loadSelection() {
        try {
          return JSON.parse(sessionStorage.getItem(storageKey) || 'null');
        } catch (e) {
          return null;
        }
      }

      populateMakes(makeSelect, makes);
      updateSubmitState();

      // Three-level cascade: Engine Model only ever reflects products that
      // match the selected Make; Part Type reflects products that match
      // both the selected Make AND the selected Model (or just Make, if
      // Model is still "All"). Changing Make always resets Model and Type
      // back to "All" since neither is guaranteed to still be valid.
      function applyMakeSelection() {
        var make = makeSelect.value;
        if (!make) {
          resetDependentField(modelSelect, ALL_MODELS_LABEL, true);
          resetDependentField(partSelect, ALL_TYPES_LABEL, true);
          updateSubmitState();
          return;
        }
        var models = getModelsForMake(allProducts, make);
        var types = getTypesForSelection(allProducts, make, '');
        console.log('[Product finder] "' + make + '" selected — engine models:', models, 'part types:', types);
        populateDependentField(modelSelect, models, ALL_MODELS_LABEL);
        populateDependentField(partSelect, types, ALL_TYPES_LABEL);
        updateSubmitState();
      }

      // Selecting a specific Model narrows Part Type down to only products
      // matching Make + that Model. Picking "All Engine Models" widens Part
      // Type back out to everything under the current Make.
      function applyModelSelection() {
        var make = makeSelect.value;
        var model = modelSelect.value;
        var types = getTypesForSelection(allProducts, make, model);
        console.log('[Product finder] "' + make + '" + "' + (model || 'All Engine Models') + '" — part types:', types);
        populateDependentField(partSelect, types, ALL_TYPES_LABEL);
        updateSubmitState();
      }

      makeSelect.addEventListener('change', function () {
        applyMakeSelection();
        saveSelection();
      });
      modelSelect.addEventListener('change', function () {
        applyModelSelection();
        saveSelection();
      });
      partSelect.addEventListener('change', function () {
        updateSubmitState();
        saveSelection();
      });

      // Browsers try to restore a <select>'s previously-picked value on
      // back/forward navigation on their own (native form-state
      // restoration, separate from bfcache) — but that never fires a
      // 'change' event, is timing-dependent relative to when this script
      // populates the options, and in practice landed on stale/wrong
      // values (e.g. re-showing an earlier test selection instead of the
      // actual last pick). Don't rely on it at all: force Make back to
      // the placeholder first so any such native restoration is
      // overwritten, then explicitly re-apply our own last-known
      // selection from sessionStorage — a single, deterministic source
      // of truth we fully control.
      function resyncFromSavedSelection() {
        makeSelect.value = '';

        var saved = loadSelection();
        if (!saved || !saved.make) {
          updateSubmitState();
          return;
        }

        makeSelect.value = saved.make;
        if (makeSelect.value !== saved.make) {
          // Saved make no longer exists among current options.
          updateSubmitState();
          return;
        }
        applyMakeSelection();

        if (saved.model) {
          modelSelect.value = saved.model;
          if (modelSelect.value === saved.model) applyModelSelection();
        }
        if (saved.type) {
          partSelect.value = saved.type; // no-op if that option no longer exists
        }
        updateSubmitState();
      }
      resyncFromSavedSelection();
      window.addEventListener('pageshow', resyncFromSavedSelection);

      form.addEventListener('submit', function (evt) {
        evt.preventDefault();
        if (!makeSelect.value) return; // Equipment Make is required

        var url = buildFilteredUrl(collectionUrl, {
          equipment_make: makeSelect.value,
          engine_model: modelSelect.value,
          part_type: partSelect.value,
        });
        window.location.href = url;
      });

      function updateSubmitState() {
        // Only Equipment Make is required — button enables the moment it's
        // set, regardless of Engine Model / Part Type state.
        submitBtn.disabled = !makeSelect.value;
      }
    });
  }

  function readPageData(blockId) {
    var dataScript = document.getElementById('FinderData-' + blockId);
    if (!dataScript) {
      console.warn('[Product finder] #FinderData-' + blockId + ' script tag not found on the page — hero-product-finder-data.liquid did not render.');
      return null;
    }
    try {
      return JSON.parse(dataScript.textContent || '{}');
    } catch (e) {
      console.error('[Product finder] could not parse finder data JSON', e, dataScript.textContent);
      return null;
    }
  }

  // Fetches every remaining page (2..pages_total) via the Section Rendering
  // API and merges all products into one array.
  function loadAllPages(page1, scanUrl, sectionId, blockId) {
    console.log('[Product finder] page 1 of ' + page1.pages_total + ' (' + page1.products_count + ' products in "' + page1.collection_handle + '"), ' + page1.products.length + ' with equipment_make set on this page');

    var allProducts = page1.products.slice();
    var pagesTotal = page1.pages_total || 1;

    if (pagesTotal <= 1 || !scanUrl || !sectionId) {
      return Promise.resolve(allProducts);
    }

    var fetches = [];
    for (var p = 2; p <= pagesTotal; p++) {
      fetches.push(fetchPage(scanUrl, sectionId, blockId, p));
    }

    return Promise.all(fetches).then(function (pages) {
      pages.forEach(function (pageData) {
        if (pageData && pageData.products) {
          allProducts = allProducts.concat(pageData.products);
        }
      });
      return allProducts;
    });
  }

  function fetchPage(scanUrl, sectionId, blockId, pageNumber) {
    var url = scanUrl + (scanUrl.indexOf('?') === -1 ? '?' : '&') + 'page=' + pageNumber + '&section_id=' + encodeURIComponent(sectionId);
    return fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var script = doc.getElementById('FinderData-' + blockId);
        if (!script) {
          console.warn('[Product finder] page ' + pageNumber + ' fetch did not contain #FinderData-' + blockId);
          return null;
        }
        var pageData = JSON.parse(script.textContent || '{}');
        console.log('[Product finder] page ' + pageNumber + ' of ' + pageData.pages_total + ': ' + pageData.products.length + ' products with equipment_make set');
        return pageData;
      })
      .catch(function (e) {
        console.error('[Product finder] failed to fetch page ' + pageNumber, e);
        return null;
      });
  }

  // Trims equipment_make/engine_model/part_type on every product so a
  // stray leading/trailing space typed into a metafield in the admin never
  // makes it into option values, filter comparisons, or the redirect URL.
  function normalizeProducts(products) {
    return (products || []).map(function (product) {
      return {
        id: product.id,
        handle: product.handle,
        title: product.title,
        equipment_make: (product.equipment_make || []).map(trimStr).filter(Boolean),
        engine_model: Array.isArray(product.engine_model)
          ? product.engine_model.map(trimStr).filter(Boolean)
          : trimStr(product.engine_model),
        part_type: trimStr(product.part_type),
      };
    });
  }

  function trimStr(value) {
    return typeof value === 'string' ? value.trim() : value;
  }

  // custom.engine_model can come through as either a plain string (single
  // line text) or an array (list metafield / older single-item list data)
  // depending on how the merchant's definition is set up — normalize both
  // to a flat array of non-blank strings so callers never have to care.
  function asStringArray(value) {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (value) return [value];
    return [];
  }

  function productHasMake(product, make) {
    return (product.equipment_make || []).indexOf(make) !== -1;
  }

  function productHasModel(product, model) {
    return asStringArray(product.engine_model).indexOf(model) !== -1;
  }

  function getMakes(products) {
    var makes = [];
    products.forEach(function (product) {
      (product.equipment_make || []).forEach(function (make) {
        if (make && makes.indexOf(make) === -1) makes.push(make);
      });
    });
    return makes.sort();
  }

  // Engine Models for products that have the given Equipment Make.
  function getModelsForMake(products, make) {
    var models = [];
    products.forEach(function (product) {
      if (!productHasMake(product, make)) return;
      asStringArray(product.engine_model).forEach(function (model) {
        if (models.indexOf(model) === -1) models.push(model);
      });
    });
    return models;
  }

  // Part Types for products matching Equipment Make, further narrowed to
  // Engine Model when one is selected (model === '' means "All Engine
  // Models" — don't filter on it).
  function getTypesForSelection(products, make, model) {
    var types = [];
    products.forEach(function (product) {
      if (!productHasMake(product, make)) return;
      if (model && !productHasModel(product, model)) return;
      if (product.part_type && types.indexOf(product.part_type) === -1) {
        types.push(product.part_type);
      }
    });
    return types;
  }

  function populateMakes(select, makes) {
    makes.forEach(function (make) {
      var opt = document.createElement('option');
      opt.value = make;
      opt.textContent = make;
      select.appendChild(opt);
    });
  }

  function populateDependentField(select, values, allLabel) {
    select.innerHTML = '';
    var allOpt = document.createElement('option');
    allOpt.value = '';
    allOpt.textContent = allLabel;
    allOpt.selected = true;
    select.appendChild(allOpt);

    // NOTE: Array.prototype.slice.call(new Set(...)) does NOT work — a Set
    // has no numeric indices/.length, so slice.call silently returns [].
    // Array.from() is the correct way to flatten a Set back to an array.
    var unique = Array.from(new Set(values || [])).sort();
    unique.forEach(function (value) {
      var opt = document.createElement('option');
      opt.value = value;
      opt.textContent = value;
      select.appendChild(opt);
    });

    // Once an Equipment Make is selected, this field is always usable —
    // even with zero real options it still has "All ..." to submit with.
    // Disabling it here (as before) made it look broken/dead rather than
    // "there's nothing to narrow by yet."
    select.disabled = false;
  }

  function resetDependentField(select, allLabel, disabled) {
    select.innerHTML = '';
    var allOpt = document.createElement('option');
    allOpt.value = '';
    allOpt.textContent = allLabel;
    allOpt.selected = true;
    select.appendChild(allOpt);
    select.disabled = disabled;
  }

  function buildFilteredUrl(baseUrl, selections) {
    var url = new URL(baseUrl, window.location.origin);

    Object.keys(EXTRA_PARAMS).forEach(function (key) {
      url.searchParams.set(key, EXTRA_PARAMS[key]);
    });

    // part_type first, then equipment_make, then engine_model — matches
    // the confirmed working param order from the live collection page.
    ['part_type', 'equipment_make', 'engine_model'].forEach(function (key) {
      var value = selections[key];
      if (!value) return; // optional field left on "All" — omit the param
      var param = FILTER_PARAM_MAP[key];
      if (!param) return;
      url.searchParams.set(param, value);
    });

    return url.toString();
  }

  return { init: init };
})();
